import { apiFetch } from "../api/client";
import {
  fetchForms,
  fieldOf,
  type Form,
  type Question,
  type RawModel,
  type Width,
} from "./forms";

/**
 * RÉÉCRIRE UN FORMULAIRE — le placement des champs, et rien d'autre.
 *
 * `PUT /domains/:d/forms/:id` avec `{ form, models }`. Il n'écrit que `FormModels` :
 * la QUESTION elle-même (`Translations` — sa clé, son libellé, ses options, ses
 * quotas) est partagée par tous les formulaires du domaine et se règle au back
 * office. Ici on décide seulement comment elle apparaît DANS CE formulaire : quel
 * composant, obligatoire ou non, verrouillée ou non, dans quel ordre, à quelle
 * largeur.
 *
 * ⚠️ LA ROUTE SUPPRIME PUIS RÉINSÈRE. Son code est sans ambiguïté :
 *
 *     await knex('FormModels').where({ formId }).del();
 *     await Promise.all(models.map((m, i) => insert({ ...m, order: i, formId })));
 *
 * Ce qu'on n'envoie pas n'est pas « laissé tel quel », il est DÉTRUIT. Tout ce
 * fichier découle de cette phrase.
 */

/** Ce que l'éditeur sait modifier sur un placement. Tout le reste voyage intact. */
export type Edit = {
  width?: Width;
  component?: string;
  required?: boolean;
  locked?: boolean;
  /**
   * Le libellé PROPRE À CE FORMULAIRE. Vide = pas d'exception, et le champ reprend le
   * libellé de la question partagée — voir `retouche`, qui l'écrit alors `null`.
   */
  label?: string;
  /** Les choix l'un sous l'autre plutôt que côte à côte (`metadata.valuesColumn`). */
  column?: boolean;
  /** Pièce jointe : ce qu'on accepte (`metadata.accept`, vide = tout) et le plafond en Mo. */
  accept?: string;
  maxMb?: number;
  /**
   * N'afficher ce champ que si un autre a reçu l'une de ces réponses : la question
   * parente (`metadata.conditionModelId`) et les clés de ses options
   * (`metadata.conditionKeys`). `null` retire la condition — les DEUX clés, sinon un
   * parent orphelin resterait, que la V1 ignore mais que l'éditeur réafficherait.
   */
  condition?: { modelId: number; keys: string[] } | null;
};

/**
 * ⚠️ ON PART DE LA LIGNE BRUTE, JAMAIS D'UN `Field`. `fieldOf` ne retient que ce que
 * l'écran affiche ; il laisse tomber `format` (les majuscules automatiques), et dans
 * `metadata` : `css`, `conditionModelId`, `conditionKeys`, `valuesColumn`,
 * `keysToHide`, `uid`. Autant de réglages faits au back office qu'une réécriture
 * depuis `Field` effacerait — sans erreur, sans trace, à la première sauvegarde.
 *
 * `metadata` est FUSIONNÉE et non remplacée, pour la même raison : on n'y ajoute
 * qu'une clé.
 */
function edit(raw: RawModel, patch: Edit): RawModel {
  const {
    width, component, required, locked, label, column, accept, maxMb, condition,
  } = patch;
  /* Les réglages qui vivent dans `metadata` se fusionnent EN UNE FOIS, sinon le
     second recopierait le `metadata` d'origine et effacerait le premier. */
  const touchesMeta =
    width !== undefined ||
    column !== undefined ||
    accept !== undefined ||
    maxMb !== undefined ||
    condition !== undefined;
  let meta: { metadata?: RawModel["metadata"] } = {};
  if (touchesMeta) {
    const merged: Record<string, unknown> = {
      ...(raw.metadata ?? {}),
      ...(width !== undefined ? { width } : {}),
      ...(column !== undefined ? { valuesColumn: column } : {}),
      ...(accept !== undefined ? { accept } : {}),
      ...(maxMb !== undefined ? { maxMb } : {}),
    };
    if (condition === null) {
      delete merged["conditionModelId"];
      delete merged["conditionKeys"];
    } else if (condition !== undefined) {
      merged["conditionModelId"] = condition.modelId;
      merged["conditionKeys"] = condition.keys;
    }
    meta = { metadata: merged as RawModel["metadata"] };
  }
  return {
    ...raw,
    ...(component !== undefined ? { component } : {}),
    ...(required !== undefined ? { isRequired: required } : {}),
    ...(locked !== undefined ? { isDisabled: locked } : {}),
    /**
     * ⚠️ VIDÉ, LE LIBELLÉ REDEVIENT `null` — il ne s'enregistre PAS comme chaîne vide.
     * C'est ce qui rend l'exception réversible : `null` fait retomber le champ sur le
     * libellé de la question partagée, une chaîne vide le laisserait muet pour de bon.
     * L'organisateur qui efface ce qu'il a tapé s'attend au premier, pas au second.
     */
    ...(label !== undefined ? { label: label.trim() || null } : {}),
    ...meta,
  };
}

/**
 * L'ORDRE DEMANDÉ, appliqué aux lignes brutes.
 *
 * ⚠️ TOUTE LIGNE ABSENTE DE `ordre` RESTE, à sa place relative, à la fin. Un champ que
 * la V2 ne sait pas afficher — un composant ajouté au back office après cette version
 * — n'apparaît pas dans l'éditeur, donc pas dans la liste que l'écran renvoie. Le
 * laisser tomber ici, c'est le supprimer de la base parce qu'une version du front ne
 * le connaissait pas.
 */
function reorder(models: RawModel[], order: number[]): RawModel[] {
  const rank = new Map(order.map((id, i) => [id, i]));
  /* ⚠️ `id === undefined` TESTÉ À PART, et pas un `?? -1` : les lignes ajoutées mais
     pas encore enregistrées portent des identifiants NÉGATIFS (voir `NOUVEAU`), donc
     un repli sur -1 confondait « cette ligne n'a pas d'identifiant » avec « c'est la
     première ligne ajoutée » et la rangeait à sa place. */
  const rankOf = (m: RawModel) =>
    m.id === undefined ? undefined : rank.get(m.id);

  return models
    .map((m, i) => ({ m, i }))
    .sort((a, b) => {
      const ra = rankOf(a.m);
      const rb = rankOf(b.m);
      if (ra !== undefined && rb !== undefined) return ra - rb;
      // Une ligne inconnue de l'éditeur ne remonte pas devant celles qu'il a rangées.
      if (ra !== undefined) return -1;
      if (rb !== undefined) return 1;
      return a.i - b.i;
    })
    .map(({ m }) => m);
}

/**
 * TOUT CE QUE L'ÉDITEUR A CHANGÉ, avant d'avoir rien envoyé.
 *
 * ⚠️ `supprimes` EXISTE PARCE QUE « ABSENT DE `ordre` » NE VEUT PAS DIRE « SUPPRIMÉ ».
 * Une ligne que l'éditeur n'affiche pas — un composant que cette version du front ne
 * connaît pas — est absente de `ordre` et doit SURVIVRE. Confondre les deux, c'est
 * effacer de la base un champ trop neuf parce qu'un front trop vieux l'ignorait. La
 * suppression se demande donc explicitement.
 */
export type Edits = {
  patches: Map<number, Edit>;
  order: number[];
  removed: ReadonlySet<number>;
  /** Les poses ajoutées, avec un identifiant négatif tant qu'elles ne sont pas en base. */
  additions: RawModel[];
};

/**
 * LE FORMULAIRE PROJETÉ — les lignes brutes telles qu'elles partiront.
 *
 * Un seul endroit décide de ce que devient le formulaire, et l'aperçu comme
 * l'enregistrement s'en servent. Deux calculs séparés auraient fini par diverger, et
 * la divergence n'aurait été visible qu'après avoir enregistré.
 */
export function project(base: Form, edits: Edits): RawModel[] {
  const alive = base.models.filter(
    (m) => m.id === undefined || !edits.removed.has(m.id),
  );

  const withAdditions = [...alive, ...edits.additions].map((m) => {
    const patch = m.id === undefined ? undefined : edits.patches.get(m.id);
    return patch ? edit(m, patch) : m;
  });

  return reorder(withAdditions, edits.order);
}

/** Les colonnes natives qui portent un numéro de téléphone (participant et société). */
const PHONE_KEYS = new Set(["mobile", "phone"]);

/**
 * LE TYPE QU'UNE QUESTION PREND EN ARRIVANT — l'organisateur le change ensuite sur la
 * ligne, rien n'est verrouillé.
 *
 * ⚠️ UNE QUESTION À CHOIX ARRIVE EN LISTE, PAS EN TEXTE (#287). Posée en `input`, elle
 * laissait le participant taper n'importe quoi, ses choix restaient cachés jusqu'à un
 * changement de type à la main, et la réponse partait en CHAÎNE au lieu de l'objet
 * canonique `{clé: true}`. Le back office ne permet pas ce cas.
 *
 * ⚠️ UN NUMÉRO ARRIVE EN « TÉLÉPHONE », PAS EN TEXTE (#516). Posé en `input`, « Mobile »
 * laissait passer « 06 12 34 5 » sans un mot : seul `phone-number` vérifie que le
 * numéro peut exister (`checks.ts`). Une question étendue a une clé tirée au hasard,
 * d'où le filtre sur les colonnes natives.
 */
function defaultComponent(question: Question): string {
  if (question.options.length) return "select";
  if (!question.extended && PHONE_KEYS.has(question.key)) return "phone-number";
  return "input";
}

/**
 * UNE POSE TOUTE NEUVE — une question du domaine, placée dans ce formulaire.
 *
 * ⚠️ ELLE NE CRÉE AUCUNE QUESTION. `modelId` désigne une `Translations` qui existe
 * déjà ; on n'écrit qu'un `FormModels`. Créer la question elle-même toucherait tous
 * les formulaires du domaine et se fait au back office — c'est la frontière que ce
 * dépôt défend depuis le début.
 *
 * ⚠️ L'IDENTIFIANT EST NÉGATIF ET PROVISOIRE. Il ne part jamais au serveur
 * (`colonnesDeFormModels` ne renvoie pas `id`, et la route les réattribue de toute
 * façon) : il sert uniquement à ce que la ligne ait une identité dans l'éditeur —
 * pour être rangée, retouchée, retirée — avant d'exister en base.
 */
export function newPlacement(
  question: Question,
  provisionalId: number,
): RawModel {
  return {
    id: provisionalId,
    modelId: question.id,
    key: question.key,
    value: question.label,
    model: question.model,
    isExtended: question.extended,
    component: defaultComponent(question),
    label: null,
    isRequired: false,
    isDisabled: false,
    format: null,
    metadata: {},
    /**
     * ⚠️ LES CHOIX DE LA QUESTION, RECOPIÉS POUR L'APERÇU. Sans eux, un champ qu'on
     * vient d'ajouter et qu'on passe en « liste » ou en « cases » s'affichait VIDE :
     * ses options n'apparaissaient qu'après avoir enregistré ET rechargé, parce
     * qu'elles ne remontent qu'avec la jointure du serveur.
     *
     * Elles ne repartent PAS au serveur — `colonnesDeFormModels` ne les envoie pas, et
     * c'est voulu : les options appartiennent à la question (`Translations`), pas au
     * placement. Les poser ici ne sert qu'à voir juste avant d'enregistrer.
     */
    values: question.options,
  };
}

/**
 * LE FORMULAIRE TEL QU'IL SERA — retouches comprises, avant d'avoir rien enregistré.
 *
 * ⚠️ C'EST L'APERÇU, ET IL COMPTE AUTANT QUE L'ENREGISTREMENT. Régler une largeur sans
 * voir le formulaire se réorganiser, c'est régler à l'aveugle et enregistrer pour
 * savoir — alors que tout le reste du panneau se voit à la frappe. La question que se
 * pose l'organisateur n'est pas « quelle valeur ? » mais « est-ce que ça tombe bien ? »,
 * et elle n'a de réponse qu'à l'écran.
 *
 * ⚠️ NE TOUCHE QUE `fields`, JAMAIS `models`. Les lignes brutes restent celles du
 * serveur : elles sont ce que la garde compare et ce que l'enregistrement renvoie. Un
 * aperçu qui les modifierait ferait croire à un changement concurrent, ou renverrait
 * l'aperçu au lieu de la vérité.
 */
export function preview(base: Form, edits: Edits): Form {
  return {
    ...base,
    fields: project(base, edits).flatMap((m, i) => {
      const f = fieldOf(m, i);
      return f ? [f] : [];
    }),
    /* ⚠️ `models` RESTE CELUI DU SERVEUR — le `...base` s'en charge, et il ne faut
       surtout pas y toucher. C'est l'empreinte que la garde compare : y écrire le
       projet ferait croire à une modification concurrente à chaque clic. */
  };
}

/** Le formulaire a-t-il changé sous nos pieds depuis qu'on l'a chargé ? */
export class Stale extends Error {
  constructor() {
    super(
      "Ce formulaire a été modifié ailleurs depuis son ouverture. Rechargez la page avant d'enregistrer, sinon vous écraseriez ces changements.",
    );
    this.name = "Perime";
  }
}

const idsDe = (models: RawModel[]) =>
  models
    .map((m) => m.id)
    .filter((id): id is number => typeof id === "number")
    .sort((a, b) => a - b)
    .join(",");

/**
 * ⚠️ LA GARDE. Les réglages, eux, sont FUSIONNÉS par le serveur : deux personnes qui
 * en modifient deux différents ne se gênent pas. Ici c'est l'inverse — la dernière
 * écriture remplace le formulaire ENTIER. Sans ce contrôle, un organisateur qui garde
 * le panneau ouvert pendant qu'un collègue ajoute un champ au back office le supprime
 * en enregistrant, et personne n'apprend rien.
 *
 * On compare les identifiants de placement, pas un horodatage : `FormModels` n'a pas
 * de version, et ses `id` changent à CHAQUE écriture puisque la route détruit et
 * réinsère. C'est précisément ce qui en fait un bon témoin.
 */
export async function saveForm(
  domainId: number,
  eventId: number,
  form: Form,
  edits: Edits,
  signal?: AbortSignal,
): Promise<void> {
  /**
   * ⚠️ ON RELIT PAR `fetchForms`, DONC PAR LA MÊME ROUTE QUE L'ÉDITEUR. Écrire la
   * requête à la main ici a coûté un bug tout bête et parfaitement invisible en
   * test : `/domains/:d/forms` ne rend QUE les formulaires du domaine — la route
   * filtre sur `F.eventId IS NULL` — et le formulaire d'une étape appartient à
   * l'ÉVÉNEMENT. La garde ne le trouvait donc jamais, concluait qu'il avait disparu,
   * et refusait tous les enregistrements avec un message parlant d'un collègue qui
   * n'existait pas.
   *
   * Une seule fonction de lecture, et la question ne se repose pas.
   */
  const fresh = await fetchForms(domainId, eventId, signal);

  const current = fresh.find((f) => f.id === form.id);
  if (!current) throw new Stale();
  if (idsDe(current.models) !== idsDe(form.models)) throw new Stale();

  const models = project(form, edits);

  await apiFetch(`/domains/${domainId}/forms/${form.id}`, {
    api: "apix",
    method: "PUT",
    body: {
      form: { name: form.name, type: form.type },
      models: models.map(formModelColumns),
    },
    signal,
  });
}

/**
 * ── LA LISTE BLANCHE, ET POURQUOI C'EN EST UNE ──
 *
 * ⚠️ LA ROUTE INJECTE L'OBJET TEL QUEL DANS UN `INSERT` : `insert({ ...m, id:
 * undefined, order: i, formId })`. Toute clé qui n'est pas une colonne de
 * `FormModels` est donc une erreur SQL, pas un champ ignoré.
 *
 * Or la lecture (`GET .../forms`) ne rend PAS des lignes de `FormModels` : elle rend
 * une jointure. `key`, `value`, `model` et `isExtended` viennent de `Translations`,
 * `values` est le tableau des options — cinq clés qui n'existent nulle part dans la
 * table où on écrit. Renvoyer la ligne lue sans la filtrer, c'est un 400 garanti.
 *
 * D'où une liste blanche plutôt qu'une liste noire des cinq intruses : je préfère
 * qu'une colonne ajoutée un jour à `FormModels` soit oubliée ici — visible, corrigeable
 * en une ligne — plutôt qu'une clé ajoutée un jour à la jointure fasse échouer tous les
 * enregistrements.
 *
 * Les colonnes, relevées sur les migrations (`20241215045227`, `20250106074548`,
 * `20250506181608`, `20250902072756`) : `formId`, `modelId`, `isRequired`,
 * `component`, `order`, `isDisabled`, `format`, `label`, `metadata`. `formId`, `order`
 * et `id` sont posés par la route — le rang vient de la POSITION dans le tableau.
 */
function formModelColumns(m: RawModel) {
  return {
    modelId: m.modelId ?? null,
    component: m.component,
    label: m.label ?? null,
    isRequired: m.isRequired ?? false,
    isDisabled: m.isDisabled ?? false,
    format: m.format ?? null,
    metadata: m.metadata ?? {},
  };
}

export { edit, reorder, formModelColumns };
